import React, { Dispatch, useContext } from 'react'
import {
  convertNumber,
  GeckoComponentShape,
  GeckoFormattingVariant,
  GeckoLocale,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementSystem,
  GeckoMeasurementUnit,
  lookUpPreferredUnit,
} from 'types'

interface GeckoLocaleContextType {
  /** The user's current locale (for example, "en-US") */
  locale: GeckoLocale

  /** The user's current measurement system (metric or imperial) */
  measurementSystem: GeckoMeasurementSystem

  /** A callback for updating the user's current locale */
  setLocale: Dispatch<GeckoLocale>

  /** A callback for updating the user's current preferred measurement system */
  setMeasurementSystem: Dispatch<GeckoMeasurementSystem>

  /**
   * A method which formats a numeric string according to the app's current
   * locale and measurement system
   * @param input         The number to format
   * @param inputUnit     The measurement unit for that number
   * @param variant       Options for formatting a number
   * @param minimumFractionDigits The minimum number of decimal places to show. The formatter will omit trailing zeros by default, but this minimum can be used to display them if desired. If undefined, falls back on the unit's default
   * @param maximumFractionDigits The maximum number of decimal places to show. If the input number has more fraction digits, it will be rounded to this maximum number of digits. If undefined, falls back on the unit's default
   */
  formatNumber: ({
    input,
    inputUnit,
    variant,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    input: number
    inputUnit: GeckoMeasurementUnit
    variant?: GeckoFormattingVariant | 'none'
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string

  /**
   * A method which formats a numeric range (start -> end) string according to
   * the app's current locale and measurement system
   * @param start         The start of the range to format
   * @param end           The end of the range to format
   * @param inputUnit     The measurement unit for that number
   * @param variant       Options for formatting a number
   * @param minimumFractionDigits The minimum number of decimal places to show
   * @param maximumFractionDigits The maximum number of decimal places to show
   */
  formatRange: ({
    start,
    end,
    inputUnit,
    variant,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    start: number
    end: number
    inputUnit: GeckoMeasurementUnit
    variant?: GeckoFormattingVariant | 'none'
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string

  /**
   * A label component which takes in a measurement and formats it in a
   * user-friendly way, taking into account the current measurement system
   * (imperial or metric) and a number of formatting options
   * @param measurement     The measurement to format
   * @param variant         The string variant to use (affects string length)
   * @param showLoading     Whether to show a "Loading..." filler string
   * @param minimumFractionDigits The minimum number of decimal places to show
   * @param maximumFractionDigits The maximum number of decimal places to show
   */
  formatMeasurement: ({
    measurement,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    measurement: GeckoMeasurement | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined

  /**
   * A label component which takes in a measurement range and formats it in a
   * user-friendly way, taking into account the current measurement system
   * (imperial or metric) and a number of formatting options
   * @param measurement     The measurement range to format
   * @param variant         The string variant to use (affects string length)
   * @param showLoading     Whether to show a "Loading..." filler string
   * @param minimumFractionDigits The minimum number of decimal places to show
   * @param maximumFractionDigits The maximum number of decimal places to show
   */
  formatMeasurementRange: ({
    measurement,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    measurement: GeckoMeasurementRange | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined

  formatMeasurementGeometry: ({
    geometry,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    geometry: GeckoComponentShape | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined

  formatMeasurementDimension: ({
    width,
    height,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    width: GeckoMeasurement | undefined
    height: GeckoMeasurement | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined

  /**
   * A method which returns the preferred measurement unit given an input unit
   * (for example, returning "millimeters" when provided inches and the app
   * is in metric)
   * @param inputUnit    The input measurement unit
   */
  getPreferredMeasurementUnit: (
    inputUnit: GeckoMeasurementUnit
  ) => GeckoMeasurementUnit
}

/**
 * A context carrying information relevant to the app's current locale and
 * methods used to format numeric information according to that locale
 * (for example, switching between inches and millimeters)
 */
const GeckoLocaleContext = React.createContext<
  GeckoLocaleContextType | undefined
>(undefined)

const measurementSystemSettingID = 'measurement_system'

//------------------------------------------------------------------------------

/**
 * A high-level provider used to simplify questions of localization (in the
 * language and measurement system sense, not the robot inspection sense).
 * This wrapper is mostly useful for ensuring the app is showing numbers in
 * the same measurement system throughout.
 * @param children Components representing the rest of the app
 */
export const GeckoLocaleProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [locale, setLocale] = React.useState<GeckoLocale>('en-US')

  // Use a browser cookie to save the preferred measurement system
  const [measurementSystem, internalSetSystem] =
    React.useState<GeckoMeasurementSystem>(() =>
      localStorage.getItem(measurementSystemSettingID) ===
      GeckoMeasurementSystem.Metric
        ? GeckoMeasurementSystem.Metric
        : GeckoMeasurementSystem.Imperial
    )

  const setMeasurementSystem = React.useCallback(
    (system: GeckoMeasurementSystem) => {
      localStorage.setItem(measurementSystemSettingID, system)
      internalSetSystem(system)
    },
    []
  )

  const {
    formatNumber,
    formatRange,
    formatMeasurement,
    formatMeasurementRange,
    formatMeasurementGeometry,
    formatMeasurementDimension,
    getPreferredMeasurementUnit,
  } = React.useMemo(() => {
    // useMemo() rather than useCallback() to ensure a refresh on measurement system update
    const formatNumber = ({
      input,
      inputUnit,
      variant,
      minimumFractionDigits,
      maximumFractionDigits,
    }: {
      input: number
      inputUnit: GeckoMeasurementUnit
      variant?: GeckoFormattingVariant | 'none'
      minimumFractionDigits?: number
      maximumFractionDigits?: number
    }): string => {
      const inputAsMeasurement = GeckoMeasurement.CreateFromValue(
        input,
        inputUnit
      )

      const outputUnit = lookUpPreferredUnit(inputUnit, measurementSystem)
      return variant === 'none'
        ? GeckoMeasurementUnit.None.formatNumber({
            num: inputAsMeasurement.toNumber(outputUnit),
            locale,
            variant: undefined,
            minimumFractionDigits,
            maximumFractionDigits,
          })
        : outputUnit.formatNumber({
            num: inputAsMeasurement.toNumber(outputUnit),
            locale,
            variant,
            minimumFractionDigits,
            maximumFractionDigits,
          })
    }

    const formatRange = ({
      start,
      end,
      inputUnit,
      variant,
      minimumFractionDigits,
      maximumFractionDigits,
    }: {
      start: number
      end: number
      inputUnit: GeckoMeasurementUnit
      variant?: GeckoFormattingVariant | 'none'
      minimumFractionDigits?: number
      maximumFractionDigits?: number
    }): string => {
      const outputUnit = lookUpPreferredUnit(inputUnit, measurementSystem)
      const convertedStart = convertNumber(start, inputUnit, outputUnit)
      const convertedEnd = convertNumber(end, inputUnit, outputUnit)
      return variant === 'none'
        ? GeckoMeasurementUnit.None.formatRange({
            start: convertedStart,
            end: convertedEnd,
            locale,
            variant: undefined,
            minimumFractionDigits,
            maximumFractionDigits,
          })
        : outputUnit.formatRange({
            start: convertedStart,
            end: convertedEnd,
            locale,
            variant,
            minimumFractionDigits,
            maximumFractionDigits,
          })
    }

    return {
      formatNumber,
      formatRange,
      formatMeasurement: ({
        measurement,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      }: {
        measurement: GeckoMeasurement | undefined
        variant?: GeckoFormattingVariant | 'none'
        showLoading?: boolean
        minimumFractionDigits?: number
        maximumFractionDigits?: number
      }): string | undefined => {
        if (!measurement) return undefined
        if (showLoading) return 'Loading…'

        return Number.isNaN(measurement.num)
          ? '…'
          : formatNumber({
              input: measurement.num,
              inputUnit: measurement.unit,
              variant,
              minimumFractionDigits,
              maximumFractionDigits,
            })
      },
      formatMeasurementRange: ({
        measurement,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      }: {
        measurement: GeckoMeasurementRange | undefined
        variant?: GeckoFormattingVariant | 'none'
        showLoading?: boolean
        minimumFractionDigits?: number
        maximumFractionDigits?: number
      }) => {
        if (!measurement) return undefined
        if (showLoading) return 'Loading…'

        const noStart = Number.isNaN(measurement.start.num)
        const noEnd = Number.isNaN(measurement.end.num)
        if (noStart && noEnd) return undefined
        if (noStart)
          return `… - ${formatNumber({ input: measurement.start.num, inputUnit: measurement.unit, variant, minimumFractionDigits, maximumFractionDigits })}`
        if (noEnd)
          return `${formatNumber({ input: measurement.start.num, inputUnit: measurement.unit, variant, minimumFractionDigits, maximumFractionDigits })} - …`
        if (measurement.size.num === 0)
          return formatNumber({
            input: measurement.start.num,
            inputUnit: measurement.unit,
            variant,
            minimumFractionDigits,
            maximumFractionDigits,
          })

        return formatRange({
          start: measurement.start.num,
          end: measurement.end.num,
          inputUnit: measurement.unit,
          variant,
          minimumFractionDigits,
          maximumFractionDigits,
        })
      },
      formatMeasurementGeometry: ({
        geometry,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      }: {
        geometry: GeckoComponentShape | undefined
        variant?: GeckoFormattingVariant | 'none'
        showLoading?: boolean
        minimumFractionDigits?: number
        maximumFractionDigits?: number
      }) => {
        const stringX =
          geometry?.x instanceof GeckoMeasurementRange
            ? formatMeasurementRange({
                measurement: geometry?.x,
                variant,
                showLoading,
                minimumFractionDigits,
                maximumFractionDigits,
              })
            : formatMeasurement({
                measurement: geometry?.x,
                variant,
                showLoading,
                minimumFractionDigits,
                maximumFractionDigits,
              })
        const stringY =
          geometry?.y instanceof GeckoMeasurementRange
            ? formatMeasurementRange({
                measurement: geometry?.y,
                variant,
                showLoading,
                minimumFractionDigits,
                maximumFractionDigits,
              })
            : formatMeasurement({
                measurement: geometry?.y,
                variant,
                showLoading,
                minimumFractionDigits,
                maximumFractionDigits,
              })
        if (!stringX || !stringY) return undefined
        return stringX + ',' + stringY
      },
      formatMeasurementDimension: ({
        width,
        height,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      }: {
        width: GeckoMeasurement | undefined
        height: GeckoMeasurement | undefined
        variant?: GeckoFormattingVariant | 'none'
        showLoading?: boolean
        minimumFractionDigits?: number
        maximumFractionDigits?: number
      }) => {
        const stringWidth = formatMeasurement({
          measurement: width,
          variant,
          showLoading,
          minimumFractionDigits,
          maximumFractionDigits,
        })

        const stringHeight = formatMeasurement({
          measurement: height,
          variant,
          showLoading,
          minimumFractionDigits,
          maximumFractionDigits,
        })
        if (!stringHeight || !stringWidth) return undefined
        return stringWidth + ' x ' + stringHeight
      },
      getPreferredMeasurementUnit: (unit: GeckoMeasurementUnit) =>
        lookUpPreferredUnit(unit, measurementSystem),
    }
  }, [locale, measurementSystem])

  return (
    <GeckoLocaleContext.Provider
      value={{
        locale,
        measurementSystem,
        setLocale,
        setMeasurementSystem,
        formatNumber,
        formatRange,
        formatMeasurement,
        formatMeasurementRange,
        formatMeasurementGeometry,
        formatMeasurementDimension,
        getPreferredMeasurementUnit,
      }}>
      {children}
    </GeckoLocaleContext.Provider>
  )
}

export const useGeckoLocale = () => {
  const context = useContext(GeckoLocaleContext)
  if (!context)
    throw new Error('useGeckoLocale() must be used within GeckoLocaleProvider')
  return context
}
